import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  CardIdentity,
  CheckIcon,
  Close,
  CrossIcon,
} from '../../../../../../assets/icons';
import { parseKycReason } from '../../../../../../helper/kycDocumentReason';
import {
  calendarDateToLocalDate,
  formatCalendarDate,
  getVisaComplementaryTypeLabel,
  hasEndValidityMismatch,
  toCalendarDateString,
} from '../../../../../../helper/visaComplementaryDocuments';
import UseContext from '../../../../../../hooks/useContext';
import ApiDatabase from '../../../../../../server';
import { isSessionExpiredError } from '../../../../../../utils/authError';
import Banner from '../../../../../banner/banner';
import Button from '../../../../../button';
import Divider from '../../../../../divider/divider';
import ModalBody from '../../../../components/body';
import UseModal from '../../../../useModal';
import {
  DateInput,
  DocumentViewer,
  useAdminModalFiles,
  useAdminModalTempWorker,
} from '../shared';

const PRESIGN_TYPE = 'visa_complementary';

const ERROR_KEYS = {
  VISA_COMPLEMENTARY_BAD_STATE: 'badState',
  INVALID_END_VALIDITY: 'invalidEndValidity',
  REASON_NOT_FOUND: 'reasonNotFound',
  VISA_COMPLEMENTARY_NOT_FOUND: 'notFound',
};

const AI_BANNER_TYPES = {
  validated: 'info',
  rejected: 'alert',
};

const AdminModalFileVisaComplementary = () => {
  const { toggle } = UseModal();
  const {
    adminTempWorkerId,
    adminUserId,
    adminComplementaryDocumentId,
    setUpdateTempWorker,
    language,
  } = UseContext();
  const token = localStorage.getItem('xsrfToken');

  const [endValidity, setEndValidity] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [reasonId, setReasonId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const text = (key, fallback = '') =>
    language.getValue(`visaComplementary.${key}`, fallback);

  const { docs, isWaiting } = useAdminModalFiles({
    token,
    adminUserId,
    adminType: PRESIGN_TYPE,
    adminFileName: adminComplementaryDocumentId,
    enabled: !!adminUserId && !!adminComplementaryDocumentId,
  });

  const { tempWorker, user, isLoading } = useAdminModalTempWorker({
    token,
    adminTempWorkerId,
    enabled: !!adminTempWorkerId,
  });

  const complementaryDocument = useMemo(
    () =>
      (tempWorker?.visa?.complementaryDocuments ?? []).find(
        (document) =>
          String(document?._id) === String(adminComplementaryDocumentId),
      ) ?? null,
    [tempWorker, adminComplementaryDocumentId],
  );

  useEffect(() => {
    setEndValidity(
      calendarDateToLocalDate(complementaryDocument?.end_validity),
    );
  }, [complementaryDocument]);

  useEffect(() => {
    ApiDatabase.getReasonRefusesDocuments(
      { token },
      (data) => setReasons(Array.isArray(data) ? data : []),
      (err) => {
        if (err?.name !== 'CanceledError') console.log(err);
      },
    );
  }, []);

  const status = String(complementaryDocument?.validated ?? '');
  const canValidate = status === '1' || status === '3';
  const canRefuse = status === '1' || status === '2';

  const ai = complementaryDocument?.ai ?? {};
  const aiReason = parseKycReason(ai.reason);
  const datesDiffer = hasEndValidityMismatch(complementaryDocument);

  const empty = text('modal.empty', '—');
  const notRead = text('modal.notRead', 'Non lu');
  const workAuthorizationText = ai.work_authorization
    ? text(`modal.workAuthorization.${ai.work_authorization}`, notRead)
    : notRead;

  const comparisonRows = [
    {
      key: 'lastname',
      read: ai.lastname,
      profile: user?.lastname,
    },
    {
      key: 'firstname',
      read: ai.firstname,
      profile: user?.firstname,
    },
    {
      key: 'birthday',
      read: formatCalendarDate(ai.birthday),
      profile: user?.birthday,
    },
    {
      key: 'documentNumber',
      read: ai.document_number,
      profile: tempWorker?.visa?.number_visa,
    },
    {
      key: 'workAuthorization',
      read: workAuthorizationText,
      profile: '',
    },
  ];

  const showError = (err) => {
    setIsSubmitting(false);
    if (err?.name === 'CanceledError') return;
    // The api layer already signs the admin out on a real session failure.
    if (isSessionExpiredError(err)) return;
    const key = ERROR_KEYS[err?.response?.data?.errorCode] || 'generic';
    toast.error(text(`errors.${key}`));
  };

  const applyUpdatedTempWorker = (data, action) => {
    setIsSubmitting(false);
    setUpdateTempWorker({
      tempWorkerId: data?._id,
      documentType: PRESIGN_TYPE,
      action,
      tempWorker: data,
    });
    toast.success(
      text(action === 'refuse' ? 'toasts.refused' : 'toasts.accepted'),
    );
    toggle();
  };

  const submitValidation = () => {
    const endValidityDay = toCalendarDateString(endValidity);
    if (!endValidityDay) {
      toast.error(text('toasts.endValidityRequired'));
      return;
    }
    setIsSubmitting(true);
    ApiDatabase.postAcceptTempWorkerVisaComplementaryDocument(
      {
        adminTempWorkerId,
        documentId: adminComplementaryDocumentId,
        end_validity: endValidityDay,
      },
      (data) => applyUpdatedTempWorker(data, 'accept'),
      showError,
    );
  };

  const submitRefusal = () => {
    if (!reasonId) {
      toast.error(text('toasts.reasonRequired'));
      return;
    }
    setIsSubmitting(true);
    ApiDatabase.postRefuseTempWorkerVisaComplementaryDocument(
      {
        adminTempWorkerId,
        documentId: adminComplementaryDocumentId,
        reason: reasonId,
      },
      (data) => applyUpdatedTempWorker(data, 'refuse'),
      showError,
    );
  };

  const filesLabel =
    docs.length > 1
      ? text('modal.filesBoth', 'Recto & verso')
      : text('modal.filesSingle', 'Document unique');

  return (
    <ModalBody>
      <div className="flex gap-4 h-[750px]">
        <div className="flex-1 min-w-0 bg-gray-50 rounded-lg overflow-hidden">
          <DocumentViewer docs={docs} isWaiting={isWaiting} />
        </div>

        <div className="w-80 flex flex-col gap-3 bg-gray-50 rounded-lg p-4 overflow-y-auto">
          <div className="p-3 border rounded flex gap-3 bg-white">
            <div className="my-auto">
              <CardIdentity wh={24} color="#6B7280" />
            </div>
            <div className="my-auto ft-sm">
              <div className="font-medium">
                {getVisaComplementaryTypeLabel(
                  complementaryDocument?.type,
                  language,
                ) || text('modal.title')}
              </div>
              <div className="text-gray-500">{filesLabel}</div>
            </div>
          </div>

          {!isLoading && !complementaryDocument && (
            <Banner type="alert">{text('modal.notFound')}</Banner>
          )}

          {complementaryDocument && (
            <>
              {status === '2' && (
                <Banner type="info">{text('modal.validatedBanner')}</Banner>
              )}
              {status === '3' && (
                <Banner type="alert">
                  {text('modal.refusedBanner').replace(
                    '{reason}',
                    () => complementaryDocument.reason || empty,
                  )}
                </Banner>
              )}

              {aiReason.text && (
                <div className="flex flex-col gap-1">
                  <div className="ft-sm font-medium text-gray-700">
                    {text('modal.aiAnalysis')}
                  </div>
                  <Banner type={AI_BANNER_TYPES[aiReason.outcome] || 'warning'}>
                    {aiReason.text}
                  </Banner>
                </div>
              )}

              <div className="grid grid-cols-3 gap-x-2 gap-y-2 ft-sm">
                <div />
                <div className="text-gray-500">{text('modal.aiColumn')}</div>
                <div className="text-gray-500">
                  {text('modal.profileColumn')}
                </div>
                {comparisonRows.map((row) => (
                  <div className="contents" key={row.key}>
                    <div>{text(`modal.fields.${row.key}`)}</div>
                    <div className="text-gray-900 break-words">
                      {row.read || notRead}
                    </div>
                    <div className="text-gray-500 break-words">
                      {row.profile || empty}
                    </div>
                  </div>
                ))}
              </div>

              <Divider />

              <div className="flex flex-col gap-2 ft-sm">
                <div className="font-medium text-gray-700">
                  {text('modal.datesTitle')}
                </div>
                <div className="flex justify-between gap-2">
                  <div>{text('modal.currentPermitEnd')}</div>
                  <div className="text-gray-500">
                    {formatCalendarDate(tempWorker?.visa?.end_validity) ||
                      empty}
                  </div>
                </div>
                <div className="flex justify-between gap-2">
                  <div>{text('modal.aiEndValidity')}</div>
                  <div className="text-gray-900">
                    {formatCalendarDate(
                      complementaryDocument.end_validity_ai,
                    ) || notRead}
                  </div>
                </div>
                <div className="flex justify-between gap-2">
                  <div>{text('modal.userEndValidity')}</div>
                  <div className="text-gray-900">
                    {formatCalendarDate(complementaryDocument.end_validity) ||
                      empty}
                  </div>
                </div>
                {datesDiffer && (
                  <Banner type="warning">{text('modal.datesMismatch')}</Banner>
                )}
              </div>
            </>
          )}
        </div>

        <div className="w-72 flex flex-col gap-4 bg-gray-50 rounded-lg p-4 overflow-y-auto">
          <div className="flex justify-end">
            <div
              className="cursor-pointer p-1 hover:bg-gray-100 rounded transition-colors"
              onClick={toggle}
            >
              <CrossIcon wh={20} color="#374151" />
            </div>
          </div>

          {canValidate && (
            <div className="flex flex-col gap-3">
              <div className="ft-sm font-medium text-gray-700">
                {text('modal.validateTitle')}
              </div>
              <DateInput
                label={text('modal.endValidityLabel')}
                value={endValidity}
                onChange={setEndValidity}
              />
              <Button
                size="LG"
                color="PRIMARY"
                onClick={submitValidation}
                disabled={isSubmitting}
              >
                <div className="flex gap-2 justify-center">
                  <div className="my-auto">
                    <CheckIcon w={14} h={10} color="#ffff" />
                  </div>
                  <div className="my-auto">{text('modal.validate')}</div>
                </div>
              </Button>
            </div>
          )}

          {canValidate && canRefuse && <Divider />}

          {canRefuse && (
            <div className="flex flex-col gap-3">
              <div className="ft-sm font-medium text-gray-700">
                {text('modal.refuseTitle')}
              </div>
              <label className="ft-sm text-gray-500">
                <div>{text('modal.reasonLabel')}</div>
                <select
                  className="w-full h-10 pl-3 pr-8 ft-b border rounded-lg text-gray-900 bg-white"
                  value={reasonId}
                  onChange={(e) => setReasonId(e.target.value)}
                >
                  <option value="">{text('modal.reasonPlaceholder')}</option>
                  {reasons.map((reason) => (
                    <option key={reason._id} value={reason._id}>
                      {reason.name}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                size="LG"
                color="SECONDARY"
                onClick={submitRefusal}
                disabled={isSubmitting}
              >
                <div className="flex gap-2 justify-center">
                  <div className="my-auto">
                    <Close wh={20} color="#374151" />
                  </div>
                  <div className="my-auto">{text('modal.refuse')}</div>
                </div>
              </Button>
            </div>
          )}
        </div>
      </div>
    </ModalBody>
  );
};

export default AdminModalFileVisaComplementary;
